import { useEffect, useRef } from "react";

const strokeArea = (path) => Math.abs(path.reduce((sum, point, index) => {
  const next = path[(index + 1) % path.length];
  return sum + point.x * next.y - next.x * point.y;
}, 0)) / 2;

const toMapPoint = (map, position) => {
  const projection = map?.getProjection();
  const center = map?.getCenter();
  if (!projection || !center) return null;
  const rect = map.getDiv().getBoundingClientRect();
  const origin = projection.fromLatLngToPoint(center);
  const scale = 2 ** map.getZoom();
  const world = new window.google.maps.Point(origin.x + (position.clientX - rect.left - rect.width / 2) / scale,
    origin.y + (position.clientY - rect.top - rect.height / 2) / scale);
  const location = projection.fromPointToLatLng(world);
  return location && { lat: location.lat(), lng: location.lng() };
};

export const MapTraceLayer = ({ mapRef, onBegin, onComplete, onShortStroke }) => {
  const canvas = useRef(null);
  const stroke = useRef(null);
  useEffect(() => {
    const element = canvas.current;
    const resize = () => {
      if (!element) return;
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      element.width = Math.round(element.clientWidth * ratio);
      element.height = Math.round(element.clientHeight * ratio);
      element.getContext("2d").setTransform(ratio, 0, 0, ratio, 0, 0);
    };
    const observer = new ResizeObserver(resize);
    if (element) observer.observe(element);
    resize();
    return () => observer.disconnect();
  }, []);

  const draw = (path, closed = false) => {
    const element = canvas.current;
    const ctx = element?.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, element.clientWidth, element.clientHeight);
    if (!path.length) return;
    ctx.beginPath(); ctx.moveTo(path[0].x, path[0].y);
    path.slice(1).forEach((point) => ctx.lineTo(point.x, point.y));
    if (closed) { ctx.closePath(); ctx.fillStyle = "rgba(255,181,71,.17)"; ctx.fill(); }
    ctx.strokeStyle = "#ffb547"; ctx.lineWidth = 3; ctx.lineJoin = "round"; ctx.lineCap = "round";
    ctx.shadowColor = "#ffb547"; ctx.shadowBlur = 8; ctx.stroke(); ctx.shadowBlur = 0;
  };
  const localPoint = (event) => {
    const rect = canvas.current.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top,
             clientX: event.clientX, clientY: event.clientY };
  };
  const down = (event) => {
    if (stroke.current || !mapRef.current?.getProjection()) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    const point = localPoint(event);
    stroke.current = { id: event.pointerId, points: [point] };
    onBegin(); draw([point]);
  };
  const move = (event) => {
    const active = stroke.current;
    if (!active || active.id !== event.pointerId) return;
    event.preventDefault();
    const point = localPoint(event);
    const previous = active.points[active.points.length - 1];
    if (Math.hypot(point.x - previous.x, point.y - previous.y) < 5) return;
    active.points.push(point); draw(active.points);
  };
  const up = (event) => {
    const active = stroke.current;
    if (!active || active.id !== event.pointerId) return;
    stroke.current = null;
    const last = localPoint(event);
    const previous = active.points[active.points.length - 1];
    if (Math.hypot(last.x - previous.x, last.y - previous.y) >= 5) active.points.push(last);
    const path = active.points;
    if (path.length < 3 || strokeArea(path) < 180) { draw([]); onShortStroke(); return; }
    draw(path, true);
    const sampled = path.length <= 120 ? path : Array.from({ length: 120 }, (_, index) => path[Math.round(index * (path.length - 1) / 119)]);
    const coordinates = sampled.map((point) => toMapPoint(mapRef.current, point));
    if (coordinates.some((point) => !point)) { draw([]); onShortStroke(); return; }
    onComplete(coordinates);
  };
  const cancel = () => { stroke.current = null; draw([]); };
  return <div className="map-trace-layer" role="application" aria-label="Disegna una zona trascinando il dito"
    onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={cancel}
    data-testid="map-trace-layer"><canvas ref={canvas} aria-hidden="true" /></div>;
};